/**
 * @module utils
 * @description Core utility functions used throughout the application.
 * Includes class name merging, phone number formatting, and other helper functions.
 */

import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges class names with Tailwind CSS classes, removing conflicts
 * 
 * @param {...(string|undefined|null|false)} inputs - Class names to merge
 * @returns {string} Merged class names
 * 
 * @example
 * ```js
 * cn('px-4 py-2', 'bg-blue-500', isActive && 'bg-blue-600')
 * // Returns: "px-4 py-2 bg-blue-500" or "px-4 py-2 bg-blue-600" if isActive
 * ```
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a phone number string to (XXX) XXX-XXXX format
 * 
 * @param {string} value - Raw phone number input
 * @returns {string} Formatted phone number
 * 
 * @example
 * ```js
 * formatPhoneNumber('1234567890')
 * // Returns: "(123) 456-7890"
 * 
 * formatPhoneNumber('123456')
 * // Returns: "(123) 456"
 * ```
 */
export function formatPhoneNumber(value) {
  if (!value) return value;
  
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
}

/**
 * Debounces a function call
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Time to wait in milliseconds
 * @returns {Function} Debounced function
 * 
 * @example
 * ```js
 * const debouncedSearch = debounce((query) => {
 *   // Search logic here
 * }, 300);
 * ```
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export const shimmer = (w, h) => `
  <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
      <linearGradient id="g">
        <stop stop-color="#333" offset="20%" />
        <stop stop-color="#222" offset="50%" />
        <stop stop-color="#333" offset="70%" />
      </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" fill="#333" />
    <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
    <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
  </svg>
`; 